import { useState } from "react"
import {
    Box, Button, Divider, Flex, Grid, GridItem, Select,
    Spinner, Text, Textarea, useColorModeValue, useDisclosure, useToast
} from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Container, Heading, Icon, Tag } from "components"
import { API } from "config"
import { useAllDistributors } from "hooks"
import { Authenticate } from "screens"

const useConference = (distributorId) => useQuery({
    queryKey: ['stock-conference', distributorId],
    queryFn: async () => {
        const { data } = await API.get(`/stock/conference?distributor_id=${distributorId}`)
        return data
    },
    enabled: !!distributorId,
    refetchInterval: 30000,
})

export default function Conference() {
    const [distributorId, setDistributorId] = useState('')
    const [reason, setReason]               = useState('')
    const [loading, setLoading]             = useState(false)
    const [errors, setErrors]               = useState({})
    const [pendingAction, setPendingAction] = useState(null)

    const toast       = useToast()
    const queryClient = useQueryClient()
    const auth        = useDisclosure()

    const distributors = useAllDistributors()
    const warehouses   = distributors.data?.filter(d => d.is_warehouse) ?? []
    const conference   = useConference(distributorId)

    const isActive   = conference.data?.active
    const activeConf = conference.data?.conference

    const cardBg         = useColorModeValue('white',      'gray.800')
    const cardBorder     = useColorModeValue('gray.200',   'gray.700')
    const labelColor     = useColorModeValue('gray.500',   'gray.400')
    const formSectionBg  = useColorModeValue('gray.50',    'gray.750')
    const activeBg       = useColorModeValue('orange.50',  'orange.900')
    const activeBorder   = useColorModeValue('orange.200', 'orange.700')
    const inactiveBg     = useColorModeValue('green.50',   'green.900')
    const inactiveBorder = useColorModeValue('green.200',  'green.700')
    const infoRowBg      = useColorModeValue('white',      'gray.700')
    const infoRowBorder  = useColorModeValue('orange.100', 'orange.800')

    const validate = () => {
        if (reason.trim().length < 10) {
            setErrors({ reason: 'Informe o motivo da conferência (mínimo 10 caracteres)' })
            return false
        }
        setErrors({})
        return true
    }

    const handleAction = async () => {
        setLoading(true)
        try {
            if (pendingAction === 'start') {
                await API.post('/stock/conference/start', { distributor_id: distributorId, reason: reason.trim() })
                toast({ title: 'Conferência iniciada. Aprovação de pedidos bloqueada.', status: 'warning', duration: 6000, isClosable: true })
                setReason('')
            } else {
                await API.post('/stock/conference/end', { distributor_id: distributorId })
                toast({ title: 'Conferência encerrada. Pedidos liberados.', status: 'success', duration: 4000, isClosable: true })
            }
            queryClient.invalidateQueries({ queryKey: ['stock-conference', distributorId] })
        } catch (err) {
            toast({ title: err?.response?.data?.message ?? 'Erro ao processar solicitação', status: 'error', isClosable: true })
        } finally {
            setLoading(false)
            setPendingAction(null)
        }
    }

    const selectedDistributor = warehouses.find(d => d.id === distributorId)

    return (
        <Container maxW="90%" mt={10}>
            <Heading title="Conferência de Estoque" />
            <Text color={labelColor} fontSize="sm" mt={1} mb={8}>
                Bloqueia a aprovação de novos pedidos de um núcleo durante o período de conferência física.
                Somente usuários com a permissão <strong>stock_conference</strong> podem iniciar ou encerrar.
            </Text>

            <Grid templateColumns={{ base: '1fr', xl: '1fr 340px' }} gap={6} alignItems="start">

                {/* ── Painel principal ───────────────────────────────── */}
                <GridItem>
                    <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden">

                        {/* Seletor de núcleo */}
                        <Box px={7} py={5} borderBottom="1px solid" borderColor={cardBorder}>
                            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                letterSpacing="wider" color={labelColor} mb={2}>
                                Selecione o núcleo
                            </Text>
                            <Select value={distributorId}
                                onChange={e => setDistributorId(e.target.value)}
                                borderRadius="lg" maxW="420px">
                                <option value="">Selecione um núcleo de estoque</option>
                                {warehouses.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </Select>
                        </Box>

                        {/* Estado vazio */}
                        {!distributorId ? (
                            <Flex direction="column" align="center" gap={4} py={20} px={8}>
                                <Flex align="center" justify="center" w={20} h={20}
                                    bg={formSectionBg} borderRadius="2xl">
                                    <Icon name="roles" boxSize={10} color="gray.300" />
                                </Flex>
                                <Box textAlign="center">
                                    <Text fontWeight="semibold" fontSize="md" color={labelColor}>
                                        Nenhum núcleo selecionado
                                    </Text>
                                    <Text fontSize="sm" color={labelColor} mt={1}>
                                        Selecione um núcleo acima para gerenciar a conferência
                                    </Text>
                                </Box>
                            </Flex>

                        ) : conference.isFetching && !conference.data ? (
                            <Flex justify="center" py={20}><Spinner size="lg" /></Flex>

                        ) : isActive ? (
                            /* ── CONFERÊNCIA ATIVA ──────────────────── */
                            <Box p={7}>
                                {/* Banner de alerta */}
                                <Box bg={activeBg} border="1px solid" borderColor={activeBorder}
                                    borderRadius="xl" p={6} mb={6}>
                                    <Flex align="center" gap={4} mb={5}>
                                        <Flex align="center" justify="center" w={12} h={12}
                                            bg="orange.100" borderRadius="xl" flexShrink={0}>
                                            <Icon name="roles" boxSize={6} color="orange.600" />
                                        </Flex>
                                        <Box>
                                            <Text fontWeight="bold" color="orange.700" fontSize="md">
                                                Conferência em andamento
                                            </Text>
                                            <Text fontSize="sm" color="orange.500" mt={0.5}>
                                                Aprovação de pedidos deste núcleo está bloqueada
                                            </Text>
                                        </Box>
                                    </Flex>

                                    <Divider borderColor="orange.200" mb={5} />

                                    <Grid templateColumns="1fr 1fr" gap={3}>
                                        {[
                                            { label: 'Iniciada por', value: activeConf?.started_by },
                                            { label: 'Início',       value: activeConf?.started_at ? new Date(activeConf.started_at).toLocaleString('pt-BR') : '—' },
                                        ].map(({ label, value }) => (
                                            <Box key={label} bg={infoRowBg} border="1px solid"
                                                borderColor={infoRowBorder} borderRadius="lg" p={3}>
                                                <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wider"
                                                    color="orange.500" fontWeight="semibold" mb={1}>
                                                    {label}
                                                </Text>
                                                <Text fontSize="sm" fontWeight="medium">{value ?? '—'}</Text>
                                            </Box>
                                        ))}
                                    </Grid>

                                    {activeConf?.reason && (
                                        <Box bg={infoRowBg} border="1px solid" borderColor={infoRowBorder}
                                            borderRadius="lg" p={3} mt={3}>
                                            <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wider"
                                                color="orange.500" fontWeight="semibold" mb={1}>
                                                Motivo
                                            </Text>
                                            <Text fontSize="sm">{activeConf.reason}</Text>
                                        </Box>
                                    )}
                                </Box>

                                <Button w="100%" size="lg" colorScheme="green" borderRadius="xl"
                                    leftIcon={<Icon name="check" />}
                                    isLoading={loading}
                                    onClick={() => { setPendingAction('end'); auth.onOpen() }}>
                                    Encerrar conferência
                                </Button>
                            </Box>

                        ) : (
                            /* ── SEM CONFERÊNCIA ATIVA ──────────────── */
                            <Box p={7}>
                                {/* Banner ok */}
                                <Box bg={inactiveBg} border="1px solid" borderColor={inactiveBorder}
                                    borderRadius="xl" p={5} mb={6}>
                                    <Flex align="center" gap={3}>
                                        <Flex align="center" justify="center" w={10} h={10}
                                            bg="green.100" borderRadius="xl" flexShrink={0}>
                                            <Icon name="check" boxSize={5} color="green.600" />
                                        </Flex>
                                        <Box>
                                            <Text fontWeight="semibold" color="green.700">Operação normal</Text>
                                            <Text fontSize="sm" color="green.500" mt={0.5}>
                                                Nenhuma conferência ativa. Pedidos seguem o fluxo normal.
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>

                                {/* Formulário de início */}
                                <Box bg={formSectionBg} borderRadius="xl" p={6}>
                                    <Text fontWeight="semibold" fontSize="sm" mb={0.5}>Iniciar nova conferência</Text>
                                    <Text fontSize="xs" color={labelColor} mb={5}>
                                        Ao confirmar, a aprovação de novos pedidos será pausada até o encerramento.
                                    </Text>

                                    <Flex justify="space-between" mb={2}>
                                        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase"
                                            letterSpacing="wider" color={labelColor}>
                                            Motivo *
                                        </Text>
                                        <Text fontSize="xs" color={reason.length < 10 ? 'red.400' : labelColor}>
                                            {reason.length}/500
                                        </Text>
                                    </Flex>
                                    <Textarea rows={4} borderRadius="lg" bg={cardBg}
                                        placeholder="Descreva o motivo (ex: Inventário mensal, revisão de lotes vencidos...)"
                                        value={reason}
                                        onChange={e => { setReason(e.target.value); setErrors({}) }}
                                        isInvalid={!!errors.reason}
                                        mb={errors.reason ? 1 : 4}
                                    />
                                    {errors.reason && (
                                        <Text fontSize="xs" color="red.500" mb={4}>{errors.reason}</Text>
                                    )}
                                    <Button w="100%" size="lg" colorScheme="orange" borderRadius="xl"
                                        leftIcon={<Icon name="roles" />}
                                        isLoading={loading}
                                        onClick={() => { if (validate()) { setPendingAction('start'); auth.onOpen() } }}>
                                        Iniciar conferência de estoque
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </GridItem>

                {/* ── Painel lateral ─────────────────────────────────── */}
                <GridItem>
                    <Box display="flex" flexDirection="column" gap={5}>

                        {/* Status */}
                        <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden">
                            <Flex align="center" gap={2} px={6} py={4} borderBottom="1px solid" borderColor={cardBorder}>
                                <Icon name="stock" boxSize={4} color={labelColor} />
                                <Text fontWeight="semibold" fontSize="sm">Status do núcleo</Text>
                            </Flex>
                            <Box p={6} display="flex" flexDirection="column" gap={3}>
                                {[
                                    {
                                        label: 'Núcleo',
                                        value: selectedDistributor?.name ?? '—',
                                        tag:   null,
                                    },
                                    {
                                        label: 'Conferência',
                                        value: null,
                                        tag:   distributorId
                                            ? (conference.isFetching
                                                ? <Spinner size="xs" />
                                                : <Tag colorScheme={isActive ? 'orange' : 'green'} size="sm">{isActive ? 'Ativa' : 'Inativa'}</Tag>)
                                            : '—',
                                    },
                                    {
                                        label: 'Pedidos',
                                        value: null,
                                        tag:   distributorId
                                            ? <Tag colorScheme={isActive ? 'red' : 'green'} size="sm">{isActive ? 'Bloqueados' : 'Liberados'}</Tag>
                                            : '—',
                                    },
                                ].map(({ label, value, tag }) => (
                                    <Flex key={label} justify="space-between" align="center"
                                        px={4} py={3} borderRadius="lg"
                                        bg={formSectionBg} border="1px solid" borderColor={cardBorder}>
                                        <Text fontSize="sm" color={labelColor}>{label}</Text>
                                        {tag ?? <Text fontSize="sm" fontWeight="medium">{value}</Text>}
                                    </Flex>
                                ))}
                            </Box>
                        </Box>

                        {/* Como funciona */}
                        <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="2xl" overflow="hidden">
                            <Flex align="center" gap={2} px={6} py={4} borderBottom="1px solid" borderColor={cardBorder}>
                                <Icon name="fiscal" boxSize={4} color={labelColor} />
                                <Text fontWeight="semibold" fontSize="sm">Como funciona</Text>
                            </Flex>
                            <Box p={6} display="flex" flexDirection="column" gap={4}>
                                {[
                                    { step: '1', icon: 'roles',  color: 'orange', text: 'Inicie informando o motivo da conferência' },
                                    { step: '2', icon: 'stock',  color: 'blue',   text: 'Realize a contagem física do estoque do núcleo' },
                                    { step: '3', icon: 'edit',   color: 'purple', text: 'Corrija divergências com ajuste manual se necessário' },
                                    { step: '4', icon: 'check',  color: 'green',  text: 'Encerre para liberar os pedidos novamente' },
                                ].map(({ step, icon, color, text }) => (
                                    <Flex key={step} align="flex-start" gap={4}>
                                        <Flex align="center" justify="center" w={8} h={8}
                                            bg={`${color}.100`} borderRadius="lg" flexShrink={0}>
                                            <Icon name={icon} boxSize={4} color={`${color}.600`} />
                                        </Flex>
                                        <Flex align="center" flex={1} minH={8}>
                                            <Text fontSize="xs" color={labelColor} lineHeight="1.5">{text}</Text>
                                        </Flex>
                                    </Flex>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </GridItem>
            </Grid>

            <Authenticate {...auth} onSuccess={handleAction} />
        </Container>
    )
}
